import { ensureSafeReturnUrl, urlCombine, urlHasProtocol } from './url';
import { useLocation, useHistory } from 'react-router';
import { useQueryString } from './hooks';
import { useMemo } from 'react';
import { BygrModule } from '@ygtechnologies/bygr-frontend-component-library';

interface UrlConfig {
    CHANGE_MANAGEMENT_URL: string;
    AFTERMARKET_URL: string;
    QUALITY_MANAGEMENT_URL: string;
    SALES_URL: string;
}

function ensureProtocol(url: string) {
    if (!url) {
        return null;
    }

    if (!urlHasProtocol(url)) {
        return window.location.protocol + '//' + url;
    }

    return url;
}

function getModuleBaseUrl(module: BygrModule | null, config: UrlConfig) {
    switch (module) {
        case BygrModule.ChangeManagement:
            return config.CHANGE_MANAGEMENT_URL;
        case BygrModule.Aftermarket:
            return config.AFTERMARKET_URL;
        case BygrModule.QualityManagement:
            return config.QUALITY_MANAGEMENT_URL;
        case BygrModule.Sales:
            return config.SALES_URL;
        default:
            return window.location.origin;
    }
}

export const getModuleUrl = (module: BygrModule, config: UrlConfig) => {
    const url = ensureProtocol(getModuleBaseUrl(module, config));
    return url || window.location.origin;
};

function parseModule(s: string | undefined): BygrModule | null {
    const defaultModule = BygrModule.ChangeManagement;

    if (!s) {
        return null;
    }

    if (BygrModule[s]) {
        return BygrModule[s];
    }

    const aliasMapping = {
        am: BygrModule.Aftermarket,
        cm: BygrModule.ChangeManagement,
        qm: BygrModule.QualityManagement,
        sales: BygrModule.Sales,
    };

    const mappedAlias = aliasMapping[s.toLowerCase()];

    if (mappedAlias) {
        return mappedAlias;
    }

    return defaultModule;
}

function getReturnUrl(returnUrl: string | undefined, returnModule: string | undefined) {
    const module = parseModule(returnModule);

    const root = {
        module: null,
        relativeUrl: '/',
        absoluteUrl: window.location.origin,
    };

    if (!returnUrl) {
        return root;
    }

    const safeReturnUrl = ensureSafeReturnUrl(returnUrl);

    const moduleUrl = ensureProtocol(getModuleBaseUrl(module, window.APP_CONFIG));

    if (!moduleUrl) {
        return root;
    }

    return {
        module: module,
        relativeUrl: safeReturnUrl,
        absoluteUrl: urlCombine(moduleUrl, safeReturnUrl),
    };
}

export function useReturnUrl(currentModule: BygrModule) {
    const location = useLocation();
    const history = useHistory();
    const { returnUrl, returnModule } = useQueryString(location);

    const navigate = useMemo(() => {
        const ret = getReturnUrl(returnUrl, returnModule);
        if (!ret.module || ret.module === currentModule) {
            return () => {
                history.push(ret.relativeUrl);
            };
        }

        return () => {
            window.location.href = ret.absoluteUrl;
        };
    }, [returnUrl, returnModule, currentModule, history]);

    return navigate;
}
